import React, { useEffect, useMemo, useState } from 'react';
import { StatusBadge } from './StatusBadge';
import { PilotFeedback } from './PilotFeedback';
import {
  getUnifiedCopilotSuggestions,
  humanizeCopilotText,
  isQuestionEchoSituation
} from '../utils/order';

export function CopilotPanel({ orderNo, orderContext, onAsk, onFeedback, busy }) {
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastAskedQuestion, setLastAskedQuestion] = useState('');

  useEffect(() => {
    setQuestion('');
    setResult(null);
    setError(null);
    setLastAskedQuestion('');
  }, [orderNo]);

  const suggestions = useMemo(
    () => getUnifiedCopilotSuggestions(orderContext),
    [orderContext]
  );

  async function submit(nextQuestion = question) {
    const value = String(nextQuestion ?? '').trim();
    if (!orderNo || !value || busy) return;

    setQuestion(value);
    setLastAskedQuestion(value);
    setError(null);

    try {
      const response = await onAsk(value);
      setResult(response);
    } catch (err) {
      setResult(null);
      setError(
        err?.correlationId
          ? `${err.message} Correlation: ${err.correlationId}`
          : err.message
      );
    }
  }

  return (
    <aside className="copilot-panel">
      <div className="copilot-panel__top">
        <div>
          <div className="eyebrow">AI Copilot</div>
          <h2>Ask about {orderNo || 'an order'}</h2>
        </div>
        <StatusBadge tone="success">Grounded</StatusBadge>
      </div>

      <div className="copilot-routing-note">
        Ask naturally. The server selects the approved context automatically.
      </div>

      <div className="suggestion-list">
        {suggestions.map((item) => (
          <button
            key={item}
            onClick={() => submit(item)}
            disabled={!orderNo || busy}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="ask-box">
        <textarea
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={orderNo ? 'Ask anything about this order…' : 'Load an order first'}
          disabled={!orderNo || busy}
          maxLength={2000}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
        <div className="ask-box__footer">
          <span>Ctrl/⌘ + Enter to send</span>
          <button
            className="primary-button"
            disabled={!orderNo || !question.trim() || busy}
            onClick={() => submit()}
          >
            {busy ? 'Working…' : 'Ask Copilot'}
          </button>
        </div>
      </div>

      {error ? <div className="inline-error">{error}</div> : null}
      {result ? (
        <>
          <CopilotAnswer response={result} question={lastAskedQuestion} />
          <PilotFeedback response={result} onSubmit={onFeedback} />
        </>
      ) : (
        <div className="copilot-empty">
          <strong>Evidence first.</strong>
          <p>Free-form questions are routed server-side to an approved read-only context.</p>
        </div>
      )}
    </aside>
  );
}

function CopilotAnswer({ response, question }) {
  if (response?.responseKind === 'deterministic_lookup') {
    return <DeterministicLookupAnswer response={response} />;
  }

  if (response?.responseKind === 'order_brief') {
    return <OrderBriefAnswer response={response} />;
  }

  const answer = response?.answer ?? {};
  const showSituation = !isQuestionEchoSituation(answer.situation, question);

  return (
    <div className="copilot-answer">
      {showSituation ? (
        <div className="copilot-answer__situation">
          <span className="copilot-answer__situation-label">Summary</span>
          {humanizeCopilotText(answer.situation)}
        </div>
      ) : (
        <div className="copilot-answer__finding-label">Grounded findings</div>
      )}

      {(answer.evidence ?? []).length ? (
        <div className="answer-section">
          <h3>Evidence</h3>
          <div className="evidence-list">
            {answer.evidence.map((item, index) => (
              <div className="evidence-item" key={`${item.statement}-${index}`}>
                <StatusBadge tone={item.classification === 'FACT' ? 'success' : 'neutral'}>
                  {item.classification}
                </StatusBadge>
                <span>{humanizeCopilotText(item.statement)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(answer.potentialIssues ?? []).length ? (
        <div className="answer-section">
          <h3>Potential issues</h3>
          <ul>
            {answer.potentialIssues.map((item) => (
              <li key={item}>{humanizeCopilotText(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {(answer.unknowns ?? []).length ? (
        <div className="answer-section">
          <h3>Unknowns</h3>
          <ul>
            {answer.unknowns.map((item) => (
              <li key={item}>{humanizeCopilotText(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {answer.recommendedNextStep ? (
        <div className="next-step">
          <span>Recommended next step</span>
          <strong>{humanizeCopilotText(answer.recommendedNextStep)}</strong>
        </div>
      ) : null}

      <div className="answer-footer">
        <span>Confidence: <strong>{answer.confidence || '—'}</strong></span>
        <span>Routed to: {purposeLabel(response.purpose)}</span>
      </div>
    </div>
  );
}

function OrderBriefAnswer({ response }) {
  const brief = response?.brief ?? {};

  return (
    <div className="copilot-answer order-brief-answer">
      <div className="order-brief-heading">
        <span className="copilot-answer__situation-label">AI case brief</span>
        <strong>{humanizeCopilotText(brief.headline || 'Order brief')}</strong>
        {brief.currentState ? (
          <p>{humanizeCopilotText(brief.currentState)}</p>
        ) : null}
      </div>

      {(brief.whatMatters ?? []).length ? (
        <div className="answer-section">
          <h3>What matters</h3>
          <div className="evidence-list">
            {brief.whatMatters.map((item, index) => (
              <div className="evidence-item" key={`${item.statement}-${index}`}>
                <StatusBadge tone={item.classification === 'FACT' ? 'success' : 'neutral'}>
                  {item.classification}
                </StatusBadge>
                <span>{humanizeCopilotText(item.statement)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(brief.currentBlockers ?? []).length ? (
        <div className="answer-section order-brief-blockers">
          <h3>Current blockers</h3>
          <ul>
            {brief.currentBlockers.map((item) => (
              <li key={item}>{humanizeCopilotText(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {(brief.customerFacingCautions ?? []).length ? (
        <div className="answer-section order-brief-cautions">
          <h3>Customer-facing cautions</h3>
          <ul>
            {brief.customerFacingCautions.map((item) => (
              <li key={item}>{humanizeCopilotText(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {(brief.unknowns ?? []).length ? (
        <div className="answer-section">
          <h3>Unknowns</h3>
          <ul>
            {brief.unknowns.map((item) => (
              <li key={item}>{humanizeCopilotText(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {brief.suggestedInvestigation ? (
        <div className="next-step">
          <span>Suggested investigation</span>
          <strong>{humanizeCopilotText(brief.suggestedInvestigation)}</strong>
        </div>
      ) : null}

      <div className="answer-footer">
        <span>Confidence: <strong>{brief.confidence || '—'}</strong></span>
        <span>Routed to: {purposeLabel(response.purpose)}</span>
      </div>
    </div>
  );
}

function DeterministicLookupAnswer({ response }) {
  const lookup = response?.lookup ?? {};

  if (lookup.lookupType !== 'shipping_address') {
    return (
      <div className="copilot-answer">
        <div className="inline-error">Unsupported deterministic lookup response.</div>
      </div>
    );
  }

  const address = lookup.shippingAddress;
  const status = lookup.status;
  const cityRegionPostal = formatCityRegionPostal(address);

  return (
    <div className="copilot-answer deterministic-lookup-answer">
      <div className="deterministic-lookup-heading">
        <div>
          <span className="copilot-answer__situation-label">Authoritative lookup</span>
          <strong>Shipping address</strong>
        </div>
        <StatusBadge tone="neutral">No AI used</StatusBadge>
      </div>

      {status === 'FOUND' && address ? (
        <address className="shipping-address-card">
          {address.fullName ? <strong>{address.fullName}</strong> : null}
          {address.companyName ? <span>{address.companyName}</span> : null}
          {address.address1 ? <span>{address.address1}</span> : null}
          {address.address2 ? <span>{address.address2}</span> : null}
          {cityRegionPostal ? <span>{cityRegionPostal}</span> : null}
          {address.country ? <span>{address.country}</span> : null}
        </address>
      ) : status === 'NOT_RECORDED' ? (
        <div className="deterministic-lookup-message">
          No shipping address is recorded for this order.
        </div>
      ) : status === 'AMBIGUOUS_MULTIPLE_RECORDS' ? (
        <div className="deterministic-lookup-message deterministic-lookup-message--warning">
          Multiple shipping-address records were found. The Copilot did not choose one automatically.
        </div>
      ) : (
        <div className="deterministic-lookup-message">
          Shipping-address status could not be determined.
        </div>
      )}

      <div className="answer-footer deterministic-lookup-footer">
        <span>Read-only · not sent to the LLM</span>
        <span>Retrieved by: Shipping address lookup</span>
      </div>
    </div>
  );
}

function formatCityRegionPostal(address) {
  const city = String(address?.city ?? '').trim();
  const state = String(address?.state ?? '').trim();
  const postal = String(address?.postalCode ?? '').trim();

  const cityState = [city, state].filter(Boolean).join(', ');
  return [cityState, postal].filter(Boolean).join(' ');
}

function purposeLabel(purpose) {
  if (purpose === 'customization') return 'Customization';
  if (purpose === 'order_delay') return 'Delay analysis';
  if (purpose === 'order_brief') return 'Case brief';
  if (purpose === 'shipping_address_lookup') return 'Shipping address lookup';
  return 'Order status';
}
